import { ExternalLink, LoaderCircle, Paperclip } from 'lucide-react';

import {
  documentBodyFromMarkdown,
  documentTitleFromMarkdown,
  type DocumentDraft,
} from '@/entities/document-draft';
import { useAttachmentViewUrlQuery } from '@/entities/file-attachments';
import {
  recipientDeliveryLabel,
  recipientLocation,
  recipientName,
  recipientOrganization,
  REFERRAL_CHANNEL_LABELS,
  type ReferralRecipient,
} from '@/entities/referral-recipient';
import { MemoizedMarkdown } from '@/shared/ui/primitives';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

interface ReferralSendDialogProps {
  draft: DocumentDraft;
  patientId: string;
  recipient: ReferralRecipient;
  open: boolean;
  sending: boolean;
  error: string | null;
  faxPreviewUrl: string | null;
  faxPreviewState: 'idle' | 'loading' | 'error';
  onOpenChange(open: boolean): void;
  onSend(): void;
}

function AttachmentPreviewLink({
  patientId,
  attachment,
}: {
  patientId: string;
  attachment: DocumentDraft['content']['attachments'][number];
}) {
  const fileType = attachment.mimeType.startsWith('image/') ? 'image' : 'document';
  const viewQuery = useAttachmentViewUrlQuery({
    userId: patientId,
    attachmentId: attachment.attachmentId,
    fileType,
  });

  return (
    <li className="flex items-center gap-2 text-xs text-muted-foreground">
      <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
      {viewQuery.isPending ? (
        <LoaderCircle className="size-3.5 shrink-0 animate-spin" aria-label="Loading preview" />
      ) : viewQuery.data?.viewUrl ? (
        <a
          href={viewQuery.data.viewUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-foreground underline"
        >
          Preview <ExternalLink className="size-3" aria-hidden="true" />
        </a>
      ) : (
        <span className="shrink-0">Preview unavailable</span>
      )}
    </li>
  );
}

/** Final clinician confirmation showing exactly who will receive the referral. */
export function ReferralSendDialog({
  draft,
  patientId,
  recipient,
  open,
  sending,
  error,
  faxPreviewUrl,
  faxPreviewState,
  onOpenChange,
  onSend,
}: ReferralSendDialogProps) {
  const subject = documentTitleFromMarkdown(draft.content.bodyMarkdown);
  const body = documentBodyFromMarkdown(draft.content.bodyMarkdown);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(42rem,calc(100vh-2rem))] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Confirm referral</DialogTitle>
          <DialogDescription>
            Review the recipient, message, and attachments before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <section className="rounded-md border bg-muted/20 p-3" aria-label="Recipient">
            <p className="font-medium">{recipientName(recipient)}</p>
            {recipient.channel === 'nppes' && recipientOrganization(recipient) ? (
              <p className="text-muted-foreground">{recipientOrganization(recipient)}</p>
            ) : null}
            <p className="text-muted-foreground">{recipientLocation(recipient)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {REFERRAL_CHANNEL_LABELS[recipient.channel]} · {recipientDeliveryLabel(recipient)}
            </p>
          </section>

          <section className="rounded-md border" aria-label="Message preview">
            {recipient.channel === 'nppes' ? (
              <div className="p-3">
                {faxPreviewState === 'loading' ? (
                  <div className="flex h-64 items-center justify-center gap-2 text-muted-foreground">
                    <LoaderCircle className="size-4 animate-spin" /> Loading fax preview…
                  </div>
                ) : faxPreviewUrl ? (
                  <iframe
                    src={`${faxPreviewUrl}#toolbar=0&navpanes=0&zoom=page-fit`}
                    title="Rendered fax preview"
                    className="h-72 w-full rounded border bg-white"
                  />
                ) : (
                  <p className="p-4 text-muted-foreground">
                    {faxPreviewState === 'error'
                      ? 'Fax preview unavailable.'
                      : 'Fax preview will appear here.'}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="border-b px-4 py-3">
                  <p className="text-xs text-muted-foreground">Subject</p>
                  <p className="font-medium">{subject || 'No subject'}</p>
                </div>
                <div className="max-h-56 overflow-y-auto px-4 py-3 text-sm leading-relaxed [&_h1]:mb-1 [&_h1]:mt-4 [&_h1]:text-sm [&_h1]:font-semibold [&_p]:my-2">
                  {body ? (
                    <MemoizedMarkdown id={`${draft.id}-send-preview`} content={body} />
                  ) : (
                    <p className="text-muted-foreground">No message yet.</p>
                  )}
                </div>
              </>
            )}
            {draft.content.attachments.length > 0 ? (
              <ul className="space-y-1 border-t px-4 py-3" aria-label="Attachments">
                {draft.content.attachments.map((attachment) => (
                  <AttachmentPreviewLink
                    key={attachment.attachmentId}
                    patientId={patientId}
                    attachment={attachment}
                  />
                ))}
              </ul>
            ) : null}
          </section>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onSend} disabled={sending}>
            {sending ? 'Sending…' : 'Confirm send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
